import { Button } from 'components';
import { DialogCustom } from 'components/DialogCustom';
import { FormMessage } from 'components/Message';
import { PartnerDataProfile } from 'components/PartnerView/PartnerDataProfile';
import { PartnerDiscount } from 'components/PartnerView/PartnerDiscount';
import { PartnerHourWork } from 'components/PartnerView/PartnerHourWorking';
import { PartnerRejectMessage } from 'components/PartnerView/PartnerRejectMessage';
import { DashboardTitle } from 'components/Titles';
import { usePartnerAdmin } from 'hooks/usePartnersAdmin';
import { useDisclose } from 'hooks/utilHooks';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Box, CircularProgress, Grid, useTheme } from '@mui/material';

interface PropsButton {
  name: string;
  disable?: boolean;
  loading?: boolean;
  onClick: () => void;
}

const ButtonMessage: React.FC<PropsButton> = ({
  name,
  disable,
  loading,
  onClick,
}) => {
  return (
    <Grid item>
      <Button disabled={disable} onClick={onClick}>
        {name}
        {loading && (
          <CircularProgress
            size="small"
            sx={{ width: 24, height: 24, ml: 2 }}
          />
        )}
      </Button>
    </Grid>
  );
};

export const PartnerView: React.FC = () => {
  const { palette } = useTheme();
  const { id } = useParams<{ id: string }>();

  const approve = useDisclose();
  const reject = useDisclose();

  const {
    getPartner,
    onSubmit,
    onSubmitReject,
    partner,
    isRejected,
    setIsRejected,
    success,
    error,
    loadAccept,
    loadReject,
  } = usePartnerAdmin();

  const form = useForm({ defaultValues: { message: '' } });

  const onReject = () => {
    setIsRejected(true);
  };

  const confirmSubmit = () => {
    reject.onOpen();
  };

  useEffect(() => {
    getPartner(id);
  }, [getPartner, id]);

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <DashboardTitle title="GESTÃO DE PARCEIROS" />
        </Grid>
        <Grid item xs={12}>
          <PartnerDataProfile
            borderOrange={palette.primary.main}
            partner={partner}
            id={id}
          />

          <Grid container mt={1} spacing={2}>
            <Grid item xs={7}>
              <PartnerDiscount
                discounts={partner?.parsedDiscounts}
                borderOrange={palette.primary.main}
              />
            </Grid>

            <Grid item xs={5}>
              <PartnerHourWork
                openingHours={partner?.workingHours}
                borderOrange={palette.primary.main}
              />
            </Grid>
          </Grid>

          {isRejected ? (
            <>
              <FormProvider {...form}>
                <PartnerRejectMessage
                  required={isRejected}
                  borderOrange={palette.primary.main}
                />
              </FormProvider>

              <Box py={1}>
                <Grid container spacing={1} justifyContent="flex-end">
                  <ButtonMessage
                    name="Voltar"
                    onClick={() => setIsRejected(false)}
                  />
                  <ButtonMessage
                    name="Enviar"
                    disable={loadReject.loading}
                    loading={loadReject.loading}
                    onClick={form.handleSubmit(confirmSubmit)}
                  />
                </Grid>
              </Box>
            </>
          ) : (
            <Box py={1}>
              <Grid container spacing={1} justifyContent="flex-end" mt={2}>
                <ButtonMessage
                  name="Recusar"
                  disable={loadReject.loading}
                  loading={loadReject.loading}
                  onClick={onReject}
                />
                <ButtonMessage
                  name="Aprovar"
                  disable={loadAccept.loading}
                  loading={loadAccept.loading}
                  onClick={form.handleSubmit(() => approve.onOpen())}
                />
              </Grid>
            </Box>
          )}

          {error && (
            <Box p={1}>
              <Grid container justifyContent="flex-end">
                <Grid item xs={6}>
                  <FormMessage data={error} severity="error" />
                </Grid>
              </Grid>
            </Box>
          )}

          {success && (
            <Box p={1}>
              <Grid container justifyContent="flex-end">
                <Grid item xs={6}>
                  <FormMessage data={success} severity="success" />
                </Grid>
              </Grid>
            </Box>
          )}
        </Grid>
      </Grid>
      <DialogCustom
        title="Aprovação de Parceiro"
        text="Deseja realmente aprovar esse parceiro?"
        isOpen={approve.isOpen}
        onClose={approve.onClose}
        onClick={() => {
          onSubmit(id);
          approve.onClose();
        }}
      />
      <DialogCustom
        title="Reprovação de Parceiro"
        text="Deseja realmente recusar esse parceiro?"
        isOpen={reject.isOpen}
        onClose={reject.onClose}
        onClick={() => {
          onSubmitReject(form.getValues(), id);
          reject.onClose();
        }}
      />
    </>
  );
};

